import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Message } from './models';
import { baseApi } from '@centerpiece/components';

export const message = baseApi
  .enhanceEndpoints({ addTagTypes: ['Messages', 'Message'] })
  .injectEndpoints({
    endpoints: builder => ({
      getMessages: builder.query<Message[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'message',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Message' as const, id })),
                'Messages',
              ]
            : ['Messages'],
      }),
      getMessage: builder.query<Message, number>({
        query: id => ({
          url: `message/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Message', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createMessage: builder.mutation({
        query: request => ({
          url: 'message',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Messages'],
      }),
      patchMessage: builder.mutation<Message, Message>({
        query: request => ({
          url: `message/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Message', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            message.util.updateQueryData('getMessage', id, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(message.util.invalidateTags(['Messages']))
             */
            dispatch(message.util.invalidateTags(['Messages']));
          }
        },
      }),
      updateMessage: builder.mutation<Message, Message>({
        query: request => ({
          url: `message/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Message', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            message.util.updateQueryData('getMessage', id, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(message.util.invalidateTags(['Messages']))
             */
            dispatch(message.util.invalidateTags(['Messages']));
          }
        },
      }),
      deleteMessage: builder.mutation<Message, number>({
        query: id => ({
          url: `message/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Message' as const, arg }, 'Messages']
            : ['Messages'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetMessagesQuery,
  useGetMessageQuery,
  useCreateMessageMutation,
  usePatchMessageMutation,
  useUpdateMessageMutation,
  useDeleteMessageMutation,
  usePrefetch: useMessagesPrefetch,
} = message;
const selectMessagesRoot = message.endpoints.getMessages.select({});
export const selectMessages = createSelector(
  selectMessagesRoot,
  message => message.data || []
);
